import { useCallback, useEffect, useState } from 'react';

/**
 * @typedef {Object} EstadoTira
 * @property {boolean} alInicio  la tira está hasta la izquierda (se apaga la flecha de atrás)
 * @property {boolean} alFinal   la tira está hasta la derecha (se apaga la flecha de adelante); si todo cabe, ambas son true
 * @property {(sentido: 1 | -1) => void} mover  recorre la tira casi un ancho completo hacia adelante (1) o atrás (-1)
 */

/**
 * Estado de una tira que se desliza de lado: dónde va (para apagar sus flechas) y cómo moverla con ellas.
 * La posición se lee una vez por cuadro mientras se desliza y también cuando cambia el ancho de la tira.
 * Con "reducir movimiento" las flechas saltan sin animación.
 * @param {import('react').RefObject<HTMLElement>} ref  la pista con overflow-x
 * @returns {EstadoTira}
 */
export default function useTira(ref) {
  const [extremos, setExtremos] = useState({ alInicio: true, alFinal: false });

  useEffect(() => {
    const pista = ref.current;
    if (!pista) return undefined;
    let raf = 0;
    const medir = () => {
      raf = 0;
      const maximo = pista.scrollWidth - pista.clientWidth;
      const alInicio = pista.scrollLeft <= 2;
      const alFinal = pista.scrollLeft >= maximo - 2;
      setExtremos((e) => (e.alInicio === alInicio && e.alFinal === alFinal ? e : { alInicio, alFinal }));
    };
    const alMoverse = () => { if (!raf) raf = requestAnimationFrame(medir); };
    medir();
    pista.addEventListener('scroll', alMoverse, { passive: true });
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(alMoverse) : null;
    if (ro) ro.observe(pista);
    return () => {
      pista.removeEventListener('scroll', alMoverse);
      if (ro) ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [ref]);

  const mover = useCallback((sentido) => {
    const pista = ref.current;
    if (!pista) return;
    const quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    pista.scrollBy({ left: sentido * pista.clientWidth * 0.8, behavior: quieto ? 'auto' : 'smooth' });
  }, [ref]);

  return { ...extremos, mover };
}
